import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QRFormValues } from "../schemas/qr-schemas";
import { UseFormReturn } from "react-hook-form";
import { translations } from "@/lib/i18n/translations";

type SocialPlatform = Extract<QRFormValues, { type: "social" }>["platform"];

interface QRFormFieldsProps {
  form: UseFormReturn<QRFormValues>;
  t: typeof translations[keyof typeof translations];
}

const textareaClass = "min-h-28 w-full resize-y border-2 border-input bg-card px-3 py-2 text-base outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 md:text-sm";

export function QRFormFields({ form, t }: QRFormFieldsProps) {
  const type = form.watch("type");

  switch (type) {
    case "url":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">URL</Label>
            <Input {...form.register("url")} id="url" type="url" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={t.forms.urlPlaceholder} />
          </div>
        </div>
      );

    case "vcard":
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t.forms.firstName}</Label>
            <Input {...form.register("firstName")} id="firstName" autoComplete="given-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">{t.forms.lastName}</Label>
            <Input {...form.register("lastName")} id="lastName" autoComplete="family-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t.forms.phone}</Label>
            <Input {...form.register("phone")} id="phone" type="tel" autoComplete="tel" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t.forms.email}</Label>
            <Input {...form.register("email")} id="email" type="email" autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization">{t.forms.organizationOptional}</Label>
            <Input {...form.register("organization")} id="organization" autoComplete="organization" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="street">{t.forms.streetOptional}</Label>
            <Input {...form.register("street")} id="street" autoComplete="street-address" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">{t.forms.cityOptional}</Label>
            <Input {...form.register("city")} id="city" autoComplete="address-level2" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">{t.forms.stateOptional}</Label>
            <Input {...form.register("state")} id="state" autoComplete="address-level1" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zip">{t.forms.zipOptional}</Label>
            <Input {...form.register("zip")} id="zip" autoComplete="postal-code" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">{t.forms.countryOptional}</Label>
            <Input {...form.register("country")} id="country" autoComplete="country-name" />
          </div>
        </div>
      );

    case "phone":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">{t.forms.phoneNumber}</Label>
            <Input {...form.register("phone")} id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder={t.forms.phonePlaceholder} />
          </div>
        </div>
      );

    case "sms":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">{t.forms.phoneNumber}</Label>
            <Input {...form.register("phone")} id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder={t.forms.phonePlaceholder} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">{t.forms.messageOptional}</Label>
            <textarea {...form.register("message")} id="message" className={textareaClass} />
          </div>
        </div>
      );

    case "email":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t.forms.emailAddress}</Label>
            <Input {...form.register("email")} id="email" type="email" autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">{t.forms.subjectOptional}</Label>
            <Input {...form.register("subject")} id="subject" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="body">{t.forms.bodyOptional}</Label>
            <textarea {...form.register("body")} id="body" className={textareaClass} />
          </div>
        </div>
      );

    case "wifi":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ssid">{t.forms.networkName}</Label>
            <Input {...form.register("ssid")} id="ssid" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t.forms.password}</Label>
            <Input {...form.register("password")} id="password" type="password" autoComplete="off" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="encryption">{t.forms.encryptionType}</Label>
            <Select onValueChange={(value: "WPA" | "WEP" | "nopass") => form.setValue("encryption", value)} value={form.watch("encryption")}>
              <SelectTrigger id="encryption" className="w-full">
                <SelectValue placeholder={t.forms.selectEncryption} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WPA">{t.forms.wpaWpa2}</SelectItem>
                <SelectItem value="WEP">{t.forms.wep}</SelectItem>
                <SelectItem value="nopass">{t.forms.none}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case "crypto":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cryptoType">{t.forms.cryptocurrency}</Label>
            <Select onValueChange={(value: "bitcoin" | "ethereum") => form.setValue("cryptoType", value)} value={form.watch("cryptoType")}>
              <SelectTrigger id="cryptoType" className="w-full">
                <SelectValue placeholder={t.forms.selectCrypto} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bitcoin">{t.forms.bitcoin}</SelectItem>
                <SelectItem value="ethereum">{t.forms.ethereum}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">{t.forms.walletAddress}</Label>
            <Input {...form.register("address")} id="address" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">{t.forms.amountOptional}</Label>
            <Input {...form.register("amount")} id="amount" type="number" step="any" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">{t.forms.labelOptional}</Label>
            <Input {...form.register("message")} id="message" />
          </div>
        </div>
      );

    case "geo":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="latitude">{t.forms.latitude}</Label>
            <Input {...form.register("latitude")} id="latitude" type="number" step="any" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="longitude">{t.forms.longitude}</Label>
            <Input {...form.register("longitude")} id="longitude" type="number" step="any" />
          </div>
        </div>
      );

    case "event":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="summary">{t.forms.eventTitle}</Label>
            <Input {...form.register("summary")} id="summary" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="startDate">{t.forms.startDate}</Label>
            <Input {...form.register("startDate")} id="startDate" type="datetime-local" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate">{t.forms.endDate}</Label>
            <Input {...form.register("endDate")} id="endDate" type="datetime-local" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t.forms.descriptionOptional}</Label>
            <textarea {...form.register("description")} id="description" className={textareaClass} />
          </div>
        </div>
      );

    case "text":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="text">{t.forms.text}</Label>
            <textarea {...form.register("text")} id="text" className={textareaClass} />
          </div>
        </div>
      );

    case "mecard":
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t.forms.name}</Label>
            <Input {...form.register("name")} id="name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t.forms.phone}</Label>
            <Input {...form.register("phone")} id="phone" type="tel" autoComplete="tel" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t.forms.emailOptional}</Label>
            <Input {...form.register("email")} id="email" type="email" />
          </div>
        </div>
      );

    case "social":
      return (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="space-y-1.5 w-full sm:w-1/2">
              <Label htmlFor="platform">{t.forms.socialPlatform}</Label>
              <Select 
                onValueChange={(value: SocialPlatform) => form.setValue("platform", value)} 
                value={form.watch("platform")}
              >
               <SelectTrigger id="platform" className="w-full">
                  <SelectValue placeholder={t.forms.selectPlatform} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="x">{t.forms.platforms.x}</SelectItem>
                  <SelectItem value="instagram">{t.forms.platforms.instagram}</SelectItem>
                  <SelectItem value="facebook">{t.forms.platforms.facebook}</SelectItem>
                  <SelectItem value="linkedin">{t.forms.platforms.linkedin}</SelectItem>
                  <SelectItem value="github">{t.forms.platforms.github}</SelectItem>
                  <SelectItem value="tiktok">{t.forms.platforms.tiktok}</SelectItem>
                  <SelectItem value="youtube">{t.forms.platforms.youtube}</SelectItem>
                  <SelectItem value="twitch">{t.forms.platforms.twitch}</SelectItem>
                  <SelectItem value="bluesky">{t.forms.platforms.bluesky}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 w-full sm:w-1/2">
              <Label htmlFor="username">{t.forms.username}</Label>
              <Input {...form.register("username")} id="username" placeholder={t.forms.usernamePlaceholder} />
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
}
